'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadStripe,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOptionEvent,
} from '@stripe/stripe-js';
import { analytics } from 'lib/analytics/analytics';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getStoreConfig } from 'lib/region/countryUtils';
import {
  STORE_TO_EXPRESS_STORE,
  STRIPE_PUBLISHABLE_KEY_BY_STORE,
  isApplePayEnabled,
  isApplePaySupportedStore,
} from 'lib/commerce/applePay/applePayHelper';

type Status = 'idle' | 'authorizing' | 'success' | 'error' | 'cancelled';

export type ExpressDisplayItem = { label: string; amount: string };

export type ExpressPaidPayload = {
  intentToken?: string;
  paymentIntentId?: string;
};

type Args = {
  store: ShopifyStore | null | undefined;
  countryCode: string | undefined;
  currencyCode: string | undefined;
  totalAmount: number;
  label: string;
  displayLineItems: ExpressDisplayItem[];
  createCart: () => Promise<string | null>;
  // Payment confirmed — earliest reliable success moment.
  onPaid?: (paid?: ExpressPaidPayload) => void;
  // Order recorded (url) or poll timed out (undefined — confirmation by email).
  onSuccess?: (confirmationUrl?: string) => void;
  onFailure?: (reason: string) => void;
};

const toCents = (amount: string | number) => Math.round(Number(amount) * 100);

// Browsers throttle repeat canMakePayment probes; remounts must not lose a confirmed answer.
const applePayAvailabilityByStore = new Map<string, boolean>();

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export const useApplePayStripe = ({
  store,
  countryCode,
  currencyCode,
  totalAmount,
  label,
  displayLineItems,
  createCart,
  onPaid,
  onSuccess,
  onFailure,
}: Args) => {
  const [status, setStatus] = useState<Status>('idle');
  // False until the paymentRequest exists — a tap before that is a dead tap.
  const [ready, setReady] = useState(false);
  // Always false on first paint so SSR never emits the Apple Pay button.
  // The module Map is restored in the mount effect below (browsers throttle
  // repeat canMakePayment probes).
  const [canUseApplePay, setCanUseApplePay] = useState(false);

  const prRef = useRef<PaymentRequest | null>(null);
  const argsRef = useRef<Args | undefined>(undefined);
  argsRef.current = {
    store,
    countryCode,
    currencyCode,
    totalAmount,
    label,
    displayLineItems,
    createCart,
    onPaid,
    onSuccess,
    onFailure,
  };
  // True while the sheet is open — update() throws then.
  const shownRef = useRef(false);
  const cartPromiseRef = useRef<Promise<string | null> | null>(null);
  const cartIdRef = useRef<string | null>(null);
  const contactRef = useRef<{
    country: string;
    region?: string | null;
    city?: string | null;
    postalCode?: string | null;
  } | null>(null);
  const sheetTotalRef = useRef(toCents(totalAmount));

  useEffect(() => {
    if (!isApplePayEnabled()) return;
    if (
      !store ||
      !isApplePaySupportedStore(store) ||
      !countryCode ||
      !currencyCode
    ) {
      return;
    }
    const pubKey = STRIPE_PUBLISHABLE_KEY_BY_STORE[store];
    if (!pubKey) return;

    let cancelled = false;
    setCanUseApplePay(applePayAvailabilityByStore.get(store) === true);

    loadStripe(pubKey)
      .then((stripe) => {
        if (!stripe || cancelled) return;

        const pr = stripe.paymentRequest({
          country: countryCode,
          currency: currencyCode.toLowerCase(),
          total: { label, amount: toCents(totalAmount) },
          displayItems: displayLineItems.map((li) => ({
            label: li.label,
            amount: toCents(li.amount),
          })),
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: true,
          requestShipping: true,
          shippingOptions: [
            { id: 'standard', label: 'Standard Shipping', detail: '', amount: 0 },
          ],
          disableWallets: ['link', 'googlePay', 'browserCard'],
        });

        // A throttled/failed re-probe never downgrades a previously-confirmed store.
        pr.canMakePayment()
          .then((result) => {
            if (cancelled) return;
            const ok = !!result?.applePay;
            if (ok) applePayAvailabilityByStore.set(store, true);
            setCanUseApplePay(
              ok || applePayAvailabilityByStore.get(store) === true,
            );
            analytics.track('applepay_can_make_payment', { applePay: ok });
          })
          .catch((err) => {
            console.warn('Apple Pay availability probe failed', err);
          });

        pr.on('shippingaddresschange', onShippingAddressChange);
        pr.on('shippingoptionchange', onShippingOptionChange);
        pr.on('paymentmethod', (ev) => onPaymentMethod(stripe, ev));
        pr.on('cancel', () => {
          shownRef.current = false;
          setStatus('cancelled');
          analytics.track('applepay_payment_failed', { reason: 'cancelled' });
        });

        prRef.current = pr;
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Stripe.js failed to load — Apple Pay unavailable', err);
        setCanUseApplePay(false);
        setReady(false);
      });

    return () => {
      cancelled = true;
      prRef.current = null;
      setReady(false);
    };
  }, [store, countryCode, currencyCode]);

  useEffect(() => {
    if (!prRef.current || shownRef.current) return;
    sheetTotalRef.current = toCents(totalAmount);
    try {
      prRef.current.update({
        total: { label, amount: toCents(totalAmount) },
        displayItems: displayLineItems.map((li) => ({
          label: li.label,
          amount: toCents(li.amount),
        })),
      });
    } catch {
      // sheet already open
    }
  }, [totalAmount, label]);

  const rateCart = async (
    storeValue: ShopifyStore,
    body: Record<string, unknown>,
  ) => {
    const resp = await fetch(
      `/api/applepay/shipping-rates/?store=${storeValue}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    const rates = await resp.json();
    if (!resp.ok || rates?.status !== 'success') return null;
    return rates as {
      shippingMethods: {
        identifier: string;
        label: string;
        detail: string;
        amount: string;
      }[];
      shippingAmount: string;
      subtotal: string;
      taxAmount: string;
      total: string;
    };
  };

  const sheetLines = (rates: {
    total: string;
    subtotal: string;
    shippingAmount: string;
    taxAmount: string;
  }) => {
    const items = [
      { label: 'Subtotal', amount: toCents(rates.subtotal) },
      { label: 'Shipping', amount: toCents(rates.shippingAmount) },
    ];
    const tax = toCents(rates.taxAmount);
    // Tax-exclusive markets (US): tax is additive and must appear as its own
    // line so the sheet sums to Shopify's charged total. Tax-inclusive (UK VAT)
    // returns taxAmount 0 from the rates API so it is not double-counted.
    if (tax > 0) items.push({ label: 'Tax', amount: tax });
    return items;
  };

  const onShippingAddressChange = async (
    event: PaymentRequestShippingAddressEvent,
  ) => {
    const current = argsRef.current;
    if (!current?.store) return event.updateWith({ status: 'fail' });
    const country = (event.shippingAddress.country ?? '').toUpperCase();
    if (!country || getStoreConfig(country).store !== current.store) {
      return event.updateWith({ status: 'invalid_shipping_address' });
    }

    try {
      const cartId = await cartPromiseRef.current;
      if (!cartId) {
        analytics.track('applepay_sheet_closed', {
          reason: 'cart_create_failed',
        });
        return event.updateWith({ status: 'fail' });
      }

      const contact = {
        country,
        region: event.shippingAddress.region,
        city: event.shippingAddress.city,
        postalCode: event.shippingAddress.postalCode,
      };
      contactRef.current = contact;

      const rates = await rateCart(current.store, {
        cartId,
        countryCode: country,
        contact,
      });
      if (!rates) {
        analytics.track('applepay_address_invalid', {
          country,
          store: current.store,
        });
        return event.updateWith({ status: 'invalid_shipping_address' });
      }

      cartIdRef.current = cartId;
      sheetTotalRef.current = toCents(rates.total);
      event.updateWith({
        status: 'success',
        total: { label: current.label, amount: toCents(rates.total) },
        displayItems: sheetLines(rates),
        shippingOptions: rates.shippingMethods.map((method) => ({
          id: method.identifier,
          label: method.label,
          detail: method.detail,
          amount: toCents(method.amount),
        })),
      });
    } catch (err) {
      console.warn('Apple Pay shipping rating failed', err);
      analytics.track('applepay_sheet_closed', { reason: 'rating_exception' });
      event.updateWith({ status: 'fail' });
    }
  };

  const onShippingOptionChange = async (
    event: PaymentRequestShippingOptionEvent,
  ) => {
    const current = argsRef.current;
    const cartId = cartIdRef.current;
    if (!current?.store || !cartId) return event.updateWith({ status: 'fail' });

    try {
      const rates = await rateCart(current.store, {
        cartId,
        countryCode: contactRef.current?.country ?? current.countryCode,
        contact: contactRef.current ?? undefined,
        shippingMethodIdentifier: event.shippingOption.id,
      });
      if (!rates) return event.updateWith({ status: 'fail' });

      sheetTotalRef.current = toCents(rates.total);
      event.updateWith({
        status: 'success',
        total: { label: current.label, amount: toCents(rates.total) },
        displayItems: sheetLines(rates),
      });
    } catch (err) {
      console.warn('Apple Pay shipping option change failed', err);
      event.updateWith({ status: 'fail' });
    }
  };

  const pollOrderStatus = async (intentToken: string) => {
    const current = argsRef.current;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      try {
        const resp = await fetch(
          `/api/applepay/order-status/?intent_token=${encodeURIComponent(
            intentToken,
          )}`,
        );
        const result = await resp.json();
        const data = result?.data ?? {};
        if (data.ready) {
          const url = data.order_token
            ? `/order/ring/${data.order_token}/`
            : data.confirmation_url;
          current?.onSuccess?.(url);
          return;
        }
        if (data.state === 'failed' || data.state === 'refunded') {
          analytics.track('applepay_payment_failed', {
            reason: 'order_failed',
          });
          current?.onFailure?.('order_failed');
          return;
        }
      } catch (err) {
        console.warn('Apple Pay order poll failed', err);
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    // Timeout is cosmetic — the confirmation email carries the same link.
    current?.onSuccess?.(undefined);
  };

  const onPaymentMethod = async (
    stripe: Awaited<ReturnType<typeof loadStripe>>,
    event: PaymentRequestPaymentMethodEvent,
  ) => {
    const current = argsRef.current;
    if (!current || !stripe) return event.complete('fail');
    setStatus('authorizing');
    analytics.track('applepay_sheet_authorized');

    const cartId = cartIdRef.current;
    const expressStore = current.store
      ? STORE_TO_EXPRESS_STORE[current.store]
      : undefined;
    if (!cartId || !expressStore) {
      shownRef.current = false;
      event.complete('fail');
      setStatus('error');
      current.onFailure?.('preconditions_not_met');
      return;
    }

    try {
      const intentResp = await fetch('/api/applepay/payment-intent/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store: expressStore,
          cart_id: cartId,
          expected_amount_cents: sheetTotalRef.current,
        }),
      });
      const intentResult = await intentResp.json();
      if (intentResp.status === 409) {
        shownRef.current = false;
        event.complete('fail');
        setStatus('error');
        analytics.track('applepay_payment_failed', { reason: 'price_changed' });
        current.onFailure?.('price_changed');
        return;
      }
      const intent = intentResult?.data;
      if (!intentResp.ok || !intent?.client_secret || !intent?.intent_token) {
        shownRef.current = false;
        event.complete('fail');
        setStatus('error');
        analytics.track('applepay_payment_failed', { reason: 'intent_failed' });
        current.onFailure?.('server_error');
        return;
      }

      // confirmCardPayment only works if the PI lives on the same account as our mount key.
      const mountKey = current.store
        ? STRIPE_PUBLISHABLE_KEY_BY_STORE[current.store]
        : undefined;
      if (
        intent.publishable_key &&
        mountKey &&
        intent.publishable_key !== mountKey
      ) {
        shownRef.current = false;
        event.complete('fail');
        setStatus('error');
        analytics.track('applepay_payment_failed', { reason: 'key_mismatch' });
        current.onFailure?.('server_error');
        return;
      }

      const addr = event.shippingAddress;
      const confirm = await stripe.confirmCardPayment(
        intent.client_secret,
        {
          payment_method: event.paymentMethod.id,
          receipt_email: event.payerEmail ?? undefined,
          shipping: {
            name: addr?.recipient ?? event.payerName ?? '',
            phone: event.payerPhone ?? addr?.phone ?? undefined,
            address: {
              line1: addr?.addressLine?.[0] ?? '',
              line2: addr?.addressLine?.[1] ?? undefined,
              city: addr?.city ?? undefined,
              state: addr?.region ?? undefined,
              postal_code: addr?.postalCode ?? undefined,
              country: addr?.country ?? undefined,
            },
          },
        },
        { handleActions: false },
      );

      if (confirm.error) {
        shownRef.current = false;
        event.complete('fail');
        setStatus('error');
        analytics.track('applepay_payment_failed', {
          reason: confirm.error.code ?? 'payment_failed',
        });
        current.onFailure?.('payment_failed');
        return;
      }

      event.complete('success');
      shownRef.current = false;

      // 3DS step-up, after the sheet closes.
      if (confirm.paymentIntent?.status === 'requires_action') {
        const action = await stripe.confirmCardPayment(intent.client_secret);
        if (action.error) {
          setStatus('error');
          analytics.track('applepay_payment_failed', {
            reason: 'authentication_failed',
          });
          current.onFailure?.('payment_failed');
          return;
        }
      }

      setStatus('success');
      analytics.track('applepay_payment_succeeded');
      current.onPaid?.({
        intentToken: intent.intent_token,
        paymentIntentId: intent.client_secret?.split('_secret_')[0],
      });

      // Fire-and-forget — the Stripe webhook covers a lost call.
      fetch('/api/applepay/confirm/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent_token: intent.intent_token }),
      }).catch(() => undefined);

      pollOrderStatus(intent.intent_token);
    } catch (err) {
      console.warn('Apple Pay express flow failed', err);
      shownRef.current = false;
      event.complete('fail');
      setStatus('error');
      analytics.track('applepay_payment_failed', { reason: 'network' });
      current.onFailure?.('network');
    }
  };

  const startSession = useCallback(() => {
    const current = argsRef.current;
    if (!prRef.current || !current) {
      argsRef.current?.onFailure?.('not_ready');
      return;
    }
    // show() must run synchronously inside the tap gesture — never await first.
    cartIdRef.current = null;
    contactRef.current = null;
    cartPromiseRef.current = current.createCart().catch((err) => {
      console.warn('Apple Pay express cart creation failed', err);
      return null;
    });
    shownRef.current = true;
    analytics.track('applepay_button_clicked');
    prRef.current.show();
  }, []);

  return { status, startSession, canUseApplePay, ready };
};
